/**
 * propertyFieldsConfig.js
 * 
 * Configuration dynamique des champs à afficher selon la classification
 * taxonomy: { operation, objet, profil }
 */

/**
 * Obtenir la configuration des champs à afficher
 * @param {Object} property - Bien avec taxonomy
 * @returns {Object} - Configuration des champs
 */
export function getFieldsConfig(property) {
  if (!property) return getDefaultConfig();
  
  const taxonomy = property.taxonomy || {};
  const operation = taxonomy.operation || property.operation || property.transaction_type;
  const objet = taxonomy.objet || property.objet;
  const profil = taxonomy.profil || property.profil;
  
  // Déterminer le type général
  const isCession = operation === 'cession';
  const isVente = operation === 'vente_immo';
  const isLocation = operation === 'location';
  const isLocationGerance = operation === 'location_gerance';
  const isLocationDroitEntree = operation === 'location_droit_entree';
  
  const isFondsCommerce = objet === 'fonds_de_commerce';
  const isLocalCommercial = objet === 'local_commercial';
  const isBureaux = objet === 'bureaux';
  const isMursCommerciaux = objet === 'murs_commerciaux';
  
  const isCHR = profil === 'chr';
  const isRetail = profil === 'retail';
  
  // Configuration de base
  const config = {
    // Sections principales
    showPrixSection: true,
    showLoyerSection: false,
    showFinanciersSection: false,
    showBailSection: false,
    showMursSection: false,
    
    // Champs Hero
    heroFields: {
      prixPrincipal: true,
      surface: true,
      ville: true,
      secteur: true
    },
    
    // Quick Bar (4 indicateurs)
    quickBar: [],
    
    // Sections détaillées
    sections: []
  };
  
  // === CESSION FONDS DE COMMERCE ===
  if (isCession && isFondsCommerce) {
    config.showFinanciersSection = true;
    config.showBailSection = true;
    
    // QuickBar : 4 KPIs fixes (afficher N/C si vide)
    config.quickBar = [
      { key: 'surface', label: 'Surface Totale', unit: 'm²', type: 'number' },
      { key: 'loyer_bail_mensuel', label: 'Loyer Mensuel', unit: '€/mois', type: 'currency' },
      { key: 'ca_annuel', label: 'C.A. ANNUEL (HT)', unit: '€', type: 'currency', color: 'emerald' },
      { key: 'rendement_brut', label: 'Rendement', unit: '%', type: 'percentage', color: 'blue' }
    ];
    
    config.sections = [
      {
        id: 'prix',
        title: 'Prix de Cession & Honoraires',
        fields: [
          { key: 'prix_cession', label: 'Prix de cession du fonds', type: 'currency' },
          { key: 'honoraires_pourcentage', label: 'Honoraires négociation', type: 'percentage' },
          { key: 'prix_net_vendeur', label: 'Net vendeur', type: 'currency' }
        ]
      },
      {
        id: 'bail',
        title: 'Bail Commercial (Charges locatives)',
        description: 'Loyer et charges que le repreneur devra payer au propriétaire des murs',
        fields: [
          { key: 'loyer_bail_mensuel', label: 'Loyer mensuel du bail', type: 'currency' },
          { key: 'loyer_bail_annuel', label: 'Loyer annuel du bail', type: 'currency' },
          { key: 'charges_mensuelles', label: 'Charges mensuelles', type: 'currency' },
          { key: 'date_debut_bail', label: 'Début du bail', type: 'date' },
          { key: 'duree_bail', label: 'Durée du bail', type: 'text' }
        ]
      },
      {
        id: 'financiers',
        title: 'Données Financières (Exploitation)',
        confidential: false,
        description: 'Informations financières de l\'exploitation',
        fields: [
          { key: 'ca_annuel', label: 'CA Annuel HT', type: 'currency' },
          { key: 'ca_n_moins_1', label: 'CA N-1', type: 'currency' },
          { key: 'ebe', label: 'EBE', type: 'currency' },
          { key: 'ebe_retraite', label: 'EBE Retraité', type: 'currency' },
          { key: 'resultat_net', label: 'Résultat Net', type: 'currency' }
        ]
      },
      {
        id: 'confidentiel',
        title: 'Informations Confidentielles',
        confidential: true,
        description: 'Informations sensibles réservées aux négociateurs',
        fields: [
          { key: 'proprietaire', label: 'Propriétaire', type: 'text' },
          { key: 'tel_proprietaire', label: 'Téléphone propriétaire', type: 'text' },
          { key: 'email_proprietaire', label: 'Email propriétaire', type: 'text' },
          { key: 'adresse_complete', label: 'Adresse complète', type: 'text' }
        ]
      }
    ];
  }
  
  // === LOCATION ===
  else if (isLocation) {
    config.showLoyerSection = true;
    config.showPrixSection = false;
    
    // QuickBar : 4 KPIs fixes
    config.quickBar = [
      { key: 'surface', label: 'Surface Totale', unit: 'm²', type: 'number' },
      { key: 'loyer_mensuel', label: 'Loyer Mensuel', unit: '€', type: 'currency', color: 'blue' },
      { key: 'charges_mensuelles', label: 'Charges', unit: '€', type: 'currency' },
      { key: 'depot_garantie', label: 'Dépôt de Garantie', unit: '€', type: 'currency' }
    ];
    
    config.sections = [
      {
        id: 'loyer',
        title: 'Loyer & Charges',
        fields: [
          { key: 'loyer_mensuel', label: 'Loyer Mensuel HT', type: 'currency' },
          { key: 'charges_mensuelles', label: 'Charges', type: 'currency' },
          { key: 'loyer_charges_incluses', label: 'Total Charges Incluses', type: 'currency' }
        ]
      },
      {
        id: 'bail',
        title: 'Conditions du Bail',
        fields: [
          { key: 'type_bail', label: 'Type de bail', type: 'text' },
          { key: 'duree_bail', label: 'Durée', type: 'text' },
          { key: 'date_disponibilite', label: 'Disponibilité', type: 'date' },
          { key: 'depot_garantie', label: 'Dépôt de garantie', type: 'currency' }
        ]
      },
      {
        id: 'confidentiel',
        title: 'Informations Confidentielles',
        confidential: true,
        description: 'Informations sensibles réservées aux négociateurs',
        fields: [
          { key: 'proprietaire', label: 'Propriétaire', type: 'text' },
          { key: 'tel_proprietaire', label: 'Téléphone propriétaire', type: 'text' },
          { key: 'email_proprietaire', label: 'Email propriétaire', type: 'text' },
          { key: 'adresse_complete', label: 'Adresse complète', type: 'text' }
        ]
      }
    ];
  }
  
  // === LOCATION AVEC PAS-DE-PORTE ===
  else if (isLocationDroitEntree) {
    config.showLoyerSection = true;
    config.showPrixSection = false;
    
    // QuickBar : 5 KPIs incluant pas-de-porte
    config.quickBar = [
      { key: 'surface', label: 'Surface Totale', unit: 'm²', type: 'number' },
      { key: 'droit_entree', label: 'Pas-de-porte', unit: '€', type: 'currency', color: 'emerald' },
      { key: 'loyer_mensuel', label: 'Loyer Mensuel', unit: '€', type: 'currency', color: 'blue' },
      { key: 'charges_mensuelles', label: 'Charges', unit: '€', type: 'currency' },
      { key: 'depot_garantie', label: 'Dépôt de Garantie', unit: '€', type: 'currency' }
    ];
    
    config.sections = [
      {
        id: 'pas_de_porte',
        title: 'Pas-de-porte & Loyer',
        fields: [
          { key: 'droit_entree', label: 'Pas-de-porte (droit d\'entrée)', type: 'currency' },
          { key: 'loyer_mensuel', label: 'Loyer Mensuel HT', type: 'currency' },
          { key: 'charges_mensuelles', label: 'Charges', type: 'currency' },
          { key: 'loyer_charges_incluses', label: 'Total Charges Incluses', type: 'currency' }
        ]
      },
      {
        id: 'bail',
        title: 'Conditions du Bail',
        fields: [
          { key: 'type_bail', label: 'Type de bail', type: 'text' },
          { key: 'duree_bail', label: 'Durée', type: 'text' },
          { key: 'date_disponibilite', label: 'Disponibilité', type: 'date' },
          { key: 'depot_garantie', label: 'Dépôt de garantie', type: 'currency' }
        ]
      },
      {
        id: 'confidentiel',
        title: 'Informations Confidentielles',
        confidential: true,
        description: 'Informations sensibles réservées aux négociateurs',
        fields: [
          { key: 'proprietaire', label: 'Propriétaire', type: 'text' },
          { key: 'tel_proprietaire', label: 'Téléphone propriétaire', type: 'text' },
          { key: 'email_proprietaire', label: 'Email propriétaire', type: 'text' },
          { key: 'adresse_complete', label: 'Adresse complète', type: 'text' }
        ]
      }
    ];
  }
  
  // === VENTE MURS COMMERCIAUX LOUÉS (Investissement) ===
  else if (isVente && isMursCommerciaux) {
    config.showMursSection = true;
    
    // QuickBar : 4 KPIs fixes (PRIX dans HERO, pas QuickBar)
    config.quickBar = [
      { key: 'surface', label: 'Surface Totale', unit: 'm²', type: 'number' },
      { key: 'loyer_existant_annuel', label: 'Loyer existant', unit: '€/an', type: 'currency' },
      { key: 'rendement_brut', label: 'Rendement Brut', unit: '%', type: 'percentage', color: 'emerald' },
      { key: 'taxe_fonciere', label: 'Taxe foncière', unit: '€', type: 'currency' }
    ];
    
    config.sections = [
      {
        id: 'prix',
        title: 'Prix de Vente & Rentabilité',
        fields: [
          { key: 'prix_annonce', label: 'Prix de vente', type: 'currency' },
          { key: 'loyer_existant_annuel', label: 'Loyer annuel existant', type: 'currency' },
          { key: 'rendement_brut', label: 'Rendement brut', type: 'percentage' },
          { key: 'charges_copro', label: 'Charges copropriété', type: 'currency' },
          { key: 'taxe_fonciere', label: 'Taxe foncière', type: 'currency' }
        ]
      },
      {
        id: 'locataire',
        title: 'Locataire en Place',
        fields: [
          { key: 'nom_locataire', label: 'Nom du locataire', type: 'text' },
          { key: 'activite_locataire', label: 'Activité', type: 'text' },
          { key: 'duree_bail_restante', label: 'Durée bail restante', type: 'text' },
          { key: 'date_fin_bail', label: 'Fin du bail', type: 'date' }
        ]
      },
      {
        id: 'confidentiel',
        title: 'Informations Confidentielles',
        confidential: true,
        description: 'Informations sensibles réservées aux négociateurs',
        fields: [
          { key: 'proprietaire', label: 'Propriétaire', type: 'text' },
          { key: 'tel_proprietaire', label: 'Téléphone propriétaire', type: 'text' },
          { key: 'email_proprietaire', label: 'Email propriétaire', type: 'text' },
          { key: 'adresse_complete', label: 'Adresse complète', type: 'text' }
        ]
      }
    ];
  }
  
  // === CESSION DROIT AU BAIL ===
  else if (isCession && objet === 'droit_au_bail') {
    config.showPrixSection = true;
    config.showBailSection = true;
    
    config.quickBar = [
      { key: 'surface', label: 'Surface', unit: 'm²', type: 'number' },
      { key: 'loyer_bail_mensuel', label: 'Loyer du bail', unit: '€/mois', type: 'currency' },
      { key: 'charges_mensuelles', label: 'Charges', unit: '€', type: 'currency' },
      { key: 'depot_garantie', label: 'Dépôt garantie', unit: '€', type: 'currency' }
    ];
    
    config.sections = [
      {
        id: 'prix',
        title: 'Prix Droit au Bail',
        fields: [
          { key: 'prix_cession', label: 'Prix du droit au bail', type: 'currency' }
        ]
      },
      {
        id: 'bail',
        title: 'Bail Commercial (Charges après cession)',
        fields: [
          { key: 'loyer_mensuel', label: 'Loyer mensuel du bail', type: 'currency' },
          { key: 'charges_mensuelles', label: 'Charges mensuelles', type: 'currency' },
          { key: 'date_debut_bail', label: 'Début du bail', type: 'date' },
          { key: 'duree_bail', label: 'Durée du bail', type: 'text' }
        ]
      }
    ];
  }
  
  // === VENTE LOCAL COMMERCIAL (LIBRE) ===
  else if (isVente && isLocalCommercial) {
    config.quickBar = [
      { key: 'surface', label: 'Surface Totale', unit: 'm²', type: 'number' },
      { key: 'prix_m2', label: 'Prix au m²', unit: '€', type: 'currency' },
      { key: 'taxe_fonciere', label: 'Taxe Foncière', unit: '€/an', type: 'currency' },
      { key: 'charges_copro', label: 'Charges Copro', unit: '€/mois', type: 'currency' }
    ];
    
    config.sections = [
      {
        id: 'prix',
        title: 'Prix & Honoraires',
        fields: [
          { key: 'prix_annonce', label: 'Prix de vente', type: 'currency' },
          { key: 'honoraires_pourcentage', label: 'Honoraires', type: 'percentage' },
          { key: 'prix_net_vendeur', label: 'Net vendeur', type: 'currency' }
        ]
      }
    ];
  }
  
  // === IMMEUBLE DE RAPPORT / INVESTISSEMENT ===
  else if ((isVente || isCession) && (profil === 'investissement' || profil === 'immeuble_rapport')) {
    config.showMursSection = true;
    
    config.quickBar = [
      { key: 'surface', label: 'Surface Totale', unit: 'm²', type: 'number' },
      { key: 'loyer_existant_annuel', label: 'Loyers totaux', unit: '€/an', type: 'currency' },
      { key: 'rendement_brut', label: 'Rendement', unit: '%', type: 'percentage', color: 'emerald' },
      { key: 'charges_copro', label: 'Charges copro', unit: '€', type: 'currency' }
    ];
    
    config.sections = [
      {
        id: 'prix',
        title: 'Prix & Rentabilité',
        fields: [
          { key: 'prix_annonce', label: 'Prix de vente', type: 'currency' },
          { key: 'loyer_existant', label: 'Loyers annuels totaux', type: 'currency' },
          { key: 'rendement_brut', label: 'Rendement brut', type: 'percentage' }
        ]
      }
    ];
  }
  
  // === TERRAIN ===
  else if (objet === 'terrain') {
    config.quickBar = [
      { key: 'surface', label: 'Surface Terrain', unit: 'm²', type: 'number' },
      { key: 'prix_m2', label: 'Prix au m²', unit: '€', type: 'currency' },
      { key: 'taxe_fonciere', label: 'Taxe foncière', unit: '€/an', type: 'currency' },
      { key: 'honoraires_pourcentage', label: 'Honoraires', unit: '%', type: 'percentage' }
    ];
    
    config.sections = [
      {
        id: 'prix',
        title: 'Prix',
        fields: [
          { key: 'prix_annonce', label: 'Prix de vente', type: 'currency' }
        ]
      }
    ];
  }
  
  // === LOCATION GÉRANCE ===
  else if (isLocationGerance) {
    config.showFinanciersSection = true;
    
    // QuickBar : 4 KPIs fixes
    config.quickBar = [
      { key: 'surface', label: 'Surface Totale', unit: 'm²', type: 'number' },
      { key: 'redevance_mensuelle', label: 'Redevance Mensuelle', unit: '€', type: 'currency', color: 'blue' },
      { key: 'ca_annuel', label: 'C.A. ANNUEL (HT)', unit: '€', type: 'currency', color: 'emerald' },
      { key: 'rendement_brut', label: 'Rendement', unit: '%', type: 'percentage' }
    ];
    
    config.sections = [
      {
        id: 'gerance',
        title: 'Location Gérance',
        fields: [
          { key: 'redevance_mensuelle', label: 'Redevance mensuelle', type: 'currency' },
          { key: 'duree_gerance', label: 'Durée de la gérance', type: 'text' },
          { key: 'caution_gerance', label: 'Caution', type: 'currency' }
        ]
      },
      {
        id: 'financiers',
        title: 'Données Financières',
        confidential: false,
        description: 'Informations financières de l\'exploitation',
        fields: [
          { key: 'ca_annuel', label: 'CA Annuel HT', type: 'currency' },
          { key: 'ebe', label: 'EBE', type: 'currency' },
          { key: 'ebe_retraite', label: 'EBE Retraité', type: 'currency' }
        ]
      },
      {
        id: 'confidentiel',
        title: 'Informations Confidentielles',
        confidential: true,
        description: 'Informations sensibles réservées aux négociateurs',
        fields: [
          { key: 'proprietaire', label: 'Propriétaire', type: 'text' },
          { key: 'tel_proprietaire', label: 'Téléphone propriétaire', type: 'text' },
          { key: 'email_proprietaire', label: 'Email propriétaire', type: 'text' },
          { key: 'adresse_complete', label: 'Adresse complète', type: 'text' }
        ]
      }
    ];
  }
  
  // === BUREAUX ===
  else if (isBureaux) {
    config.quickBar = [
      { key: 'surface', label: 'Surface Totale', unit: 'm²', type: 'number' },
      { key: 'nb_bureaux', label: 'Nombre de Bureaux', unit: '', type: 'number' },
      { key: 'nb_parkings', label: 'Parkings', unit: '', type: 'number' },
      { key: 'etage', label: 'Étage', unit: '', type: 'text' }
    ];
    
    if (isLocation) {
      config.sections = [
        {
          id: 'loyer',
          title: 'Loyer & Charges',
          fields: [
            { key: 'loyer_mensuel', label: 'Loyer Mensuel HT', type: 'currency' },
            { key: 'charges_mensuelles', label: 'Charges', type: 'currency' }
          ]
        }
      ];
    } else {
      config.sections = [
        {
          id: 'prix',
          title: 'Prix',
          fields: [
            { key: 'prix_annonce', label: 'Prix de vente', type: 'currency' }
          ]
        }
      ];
    }
  }
  
  // === FALLBACK (ancien format ou non classifié) ===
  else {
    config.quickBar = [
      { key: 'surface', label: 'Surface', unit: 'm²', type: 'number' },
      { key: 'prix_ou_loyer', label: 'Prix/Loyer', unit: '€', type: 'currency' },
      { key: 'info_1', label: 'Info 1', unit: '', type: 'text' },
      { key: 'info_2', label: 'Info 2', unit: '', type: 'text' }
    ];
  }
  
  return config;
}

/**
 * Configuration par défaut
 */
function getDefaultConfig() {
  return {
    showPrixSection: true,
    showLoyerSection: false,
    showFinanciersSection: false,
    showBailSection: false,
    heroFields: {
      prixPrincipal: true,
      surface: true,
      ville: true
    },
    quickBar: [
      { key: 'surface', label: 'Surface', unit: 'm²', type: 'number' },
      { key: 'prix', label: 'Prix', unit: '€', type: 'currency' }
    ],
    sections: []
  };
}

export default {
  getFieldsConfig
};
